import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, User, Users, ArrowRight } from "lucide-react";
import Logo from "./Logo";

const LINKS = [
    { label: "Início", to: "/" },
    { label: "Soluções", to: "/solucoes" },
    { label: "Quem Somos", to: "/quem-somos" },
    { label: "Contato", to: "/contato" },
];

const ACESSOS = [
    { label: "Cliente", href: "https://smart-vt-flow.base44.app/login?perfil=empresa", icon: User, testid: "nav-cliente-btn" },
    { label: "Funcionário", href: "https://smart-vt-flow.base44.app/login?perfil=funcionario", icon: Users, testid: "nav-funcionario-btn" },
];

function BotoesAcesso({ compact = false }) {
    return (
        <div className={`flex items-center gap-2 ${compact ? "flex-col w-full" : ""}`}>
            {ACESSOS.map((a) => (
                <a
                    key={a.href}
                    href={a.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={a.testid}
                    className={`inline-flex items-center gap-1.5 rounded-full border border-azul-200 px-3.5 py-2 text-[13px] font-semibold text-azul-700 transition-all duration-200 hover:border-azul-500 hover:bg-azul-50 ${compact ? "w-full justify-center" : ""}`}
                >
                    <a.icon className="h-3.5 w-3.5" /> {a.label}
                </a>
            ))}
        </div>
    );
}

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { pathname } = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [pathname]);

    return (
        <header
            className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${scrolled ? "glass border-b border-slate-200/70 shadow-sm" : "bg-transparent"}`}
            data-testid="navbar"
        >
            <div className="container-x flex h-[72px] items-center justify-between gap-4">
                <Link to="/" data-testid="nav-logo-link" aria-label="Smart Benefícios — Início">
                    <Logo />
                </Link>

                <nav className="hidden items-center gap-1 lg:flex" aria-label="Menu principal">
                    {LINKS.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            data-testid={`nav-link-${l.to === "/" ? "inicio" : l.to.slice(1)}`}
                            className={({ isActive }) =>
                                `rounded-full px-4 py-2 text-sm font-semibold transition-colors duration-200 ${
                                    isActive ? "bg-azul-50 text-azul-700" : "text-tinta-medio hover:text-azul-700"
                                }`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden items-center gap-3 lg:flex">
                    <BotoesAcesso />
                    <Link
                        to="/contato"
                        data-testid="nav-cta-fale-com-smart"
                        className="group inline-flex items-center gap-2 rounded-full bg-azul-500 px-5 py-2.5 text-sm font-bold text-white shadow-[0_8px_24px_-6px_rgba(13,82,206,0.5)] transition-all duration-200 hover:-translate-y-0.5 hover:bg-azul-600"
                    >
                        Fale com a Smart
                        <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                    </Link>
                </div>

                <button
                    className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-tinta lg:hidden"
                    onClick={() => setOpen((v) => !v)}
                    aria-label={open ? "Fechar menu" : "Abrir menu"}
                    data-testid="nav-mobile-toggle"
                >
                    {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: -12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -12 }}
                        transition={{ duration: 0.25 }}
                        className="glass border-b border-slate-200/70 px-5 pb-6 pt-2 lg:hidden"
                        data-testid="nav-mobile-menu"
                    >
                        <nav className="flex flex-col gap-1" aria-label="Menu principal mobile">
                            {LINKS.map((l) => (
                                <NavLink
                                    key={l.to}
                                    to={l.to}
                                    data-testid={`nav-mobile-link-${l.to === "/" ? "inicio" : l.to.slice(1)}`}
                                    className={({ isActive }) =>
                                        `rounded-xl px-4 py-3 font-display text-lg font-bold ${isActive ? "bg-azul-50 text-azul-700" : "text-tinta"}`
                                    }
                                >
                                    {l.label}
                                </NavLink>
                            ))}
                        </nav>
                        <div className="mt-4 flex flex-col gap-3">
                            <BotoesAcesso compact />
                            <Link
                                to="/contato"
                                data-testid="nav-mobile-cta"
                                className="inline-flex items-center justify-center gap-2 rounded-full bg-azul-500 px-5 py-3.5 text-base font-bold text-white"
                            >
                                Fale com a Smart <ArrowRight className="h-4 w-4" />
                            </Link>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}
